import { create } from 'zustand'
import {
  createEmptyAnamnesisContextMetadata,
  type AnamnesisContextMetadata,
} from './anamnesisContextAdapters'
import {
  sanitizeTranscriptTechnicalMetadata,
  type AnamnesisTranscriptTechnicalMetadata,
} from './TranscriptReconciliationEngine'
import type { AnamnesisSpeakerRole } from './speakerDiarizationPipeline'
import { bindAnamnesisLangfuseConsultation, unbindAnamnesisLangfuseConsultation } from '@/shared/observability/frontend-observability'
import { trackAnamnesisTechnicalEvent } from './anamnesisTechnicalTelemetry'

export type AnamnesisTranscriptStatus =
  | 'idle'
  | 'listening'
  | 'processing'
  | 'error'
  | 'permission-denied'

export type AnamnesisSemanticBlockStatus =
  | 'pending'
  | 'queued'
  | 'processed'
  | 'needs-reprocess'

export type AnamnesisAnalysisQueueReason =
  | 'new-block'
  | 'partial-reprocess'
  | 'auto-pause'
  | 'manual-quick'
  | 'provider-fallback'
  | 'provider-retry'

export type AnamnesisAnalysisStatus = 'queued' | 'processing' | 'done' | 'failed'

export interface AnamnesisSemanticBlock {
  id: string
  text: string
  source: 'speech'
  startedAt: string
  endedAt: string
  transcription?: AnamnesisTranscriptTechnicalMetadata
  /** Diarização incremental (Fase 10); opcional para compatibilidade com sessões antigas. */
  speaker?: AnamnesisSpeakerRole
  speakerConfidence?: number
  /** Identificador estável do segmento (p.ex. derivado do chunkId técnico). */
  segmentId?: string
  /** Score 0–1 de heurística vocal; nunca embedding. */
  doctorVoiceMatchScore?: number
  /** Indica que a atribuição automática estava errada (correção manual). */
  incorrectAutoGuess?: boolean
  status: AnamnesisSemanticBlockStatus
  revision: number
}

export interface AnamnesisSilenceEvent {
  id: string
  startedAt: string
  endedAt?: string
  durationMs?: number
  reason: 'browser-silence' | 'manual-pause' | 'engine-restart'
}

export interface AnamnesisAiAnalysisQueueItem {
  id: string
  blockId: string
  blockIds: string[]
  snapshotId?: string
  reason: AnamnesisAnalysisQueueReason
  status: AnamnesisAnalysisStatus
  createdAt: string
  updatedAt: string
  revision: number
  requestSignature?: string
  errorMessage?: string
}

export interface AnamnesisIncrementalAnalysisResult {
  suggestedQuestions: string[]
  insights: string[]
  gaps: string[]
  conductSuggestions: string[]
  confidenceScore: number
  promptVersion: string
}

export interface AnamnesisIncrementalAnalysisRun {
  id: string
  queueItemId: string
  snapshotId: string
  reason: Extract<AnamnesisAnalysisQueueReason, 'auto-pause' | 'manual-quick'>
  status: AnamnesisAnalysisStatus
  blockIds: string[]
  requestSignature: string
  createdAt: string
  updatedAt: string
  startedAt?: string
  completedAt?: string
  result?: AnamnesisIncrementalAnalysisResult
  errorMessage?: string
}

export type AnamnesisRealtimeSuggestionKind = 'insight' | 'question' | 'gap' | 'conduct'
export type AnamnesisRealtimeSuggestionPriority = 'critical' | 'high' | 'medium'
export type AnamnesisRealtimeSuggestionInteractionStatus =
  | 'displayed'
  | 'ignored'
  | 'performed'
  | 'responded'
  | 'reviewed'

export interface AnamnesisRealtimeSuggestionInteraction {
  key: string
  kind: AnamnesisRealtimeSuggestionKind
  priority: AnamnesisRealtimeSuggestionPriority
  status: AnamnesisRealtimeSuggestionInteractionStatus
  title: string
  text: string
  sourceRunId: string
  firstDisplayedAt: string
  lastUpdatedAt: string
  displayCount: number
  persistenceContractVersion: 1
}

export interface AnamnesisTranscriptSnapshot {
  id: string
  createdAt: string
  reason:
    | 'session-started'
    | 'transcript-confirmed'
    | 'manual-reprocess'
    | 'auto-analysis'
    | 'manual-analysis'
    | 'session-reset'
    | 'transcription-engine-switched'
    | 'transcription-fallback-used'
    | 'transcription-retry'
  confirmedLength: number
  interimLength: number
  semanticBlockCount: number
  queuedAnalysisCount: number
  contextMetadata: AnamnesisContextMetadata
  transcription?: AnamnesisTranscriptTechnicalMetadata
}

interface StartSessionOptions {
  sessionId?: string
  now?: Date
  doctorId?: string | null
  language?: string
  offlineMode?: boolean
  fallbackMode?: string | null
}

interface ConfirmTranscriptOptions {
  startedAt?: Date
  endedAt?: Date
  metadata?: AnamnesisTranscriptTechnicalMetadata
  speaker?: AnamnesisSpeakerRole
  speakerConfidence?: number
  segmentId?: string
  doctorVoiceMatchScore?: number
}

interface RecordTranscriptionTechnicalSnapshotOptions {
  reason: Extract<
    AnamnesisTranscriptSnapshot['reason'],
    'transcription-engine-switched' | 'transcription-fallback-used' | 'transcription-retry'
  >
  metadata?: AnamnesisTranscriptTechnicalMetadata
}

interface StartIncrementalAnalysisOptions {
  blockIds: string[]
  reason: Extract<AnamnesisAnalysisQueueReason, 'auto-pause' | 'manual-quick'>
  requestSignature: string
  now?: Date
}

interface CompleteIncrementalAnalysisOptions {
  result: AnamnesisIncrementalAnalysisResult
  now?: Date
}

interface FailIncrementalAnalysisOptions {
  errorMessage: string
  now?: Date
}

interface RecordSuggestionDisplayedOptions {
  key: string
  kind: AnamnesisRealtimeSuggestionKind
  priority: AnamnesisRealtimeSuggestionPriority
  title: string
  text: string
  sourceRunId: string
  now?: Date
}

interface UpdateSuggestionInteractionOptions {
  key: string
  status: AnamnesisRealtimeSuggestionInteractionStatus
  now?: Date
}

export interface AnamnesisTranscriptState {
  sessionId: string | null
  status: AnamnesisTranscriptStatus
  errorMessage: string | null
  interimText: string
  confirmedText: string
  semanticBlocks: AnamnesisSemanticBlock[]
  silenceEvents: AnamnesisSilenceEvent[]
  aiAnalysisQueue: AnamnesisAiAnalysisQueueItem[]
  analysisRuns: AnamnesisIncrementalAnalysisRun[]
  suggestionInteractions: AnamnesisRealtimeSuggestionInteraction[]
  snapshots: AnamnesisTranscriptSnapshot[]
  startSession: (options?: StartSessionOptions) => void
  setStatus: (status: AnamnesisTranscriptStatus, errorMessage?: string | null) => void
  appendInterim: (text: string) => void
  clearInterim: () => void
  confirmTranscript: (text: string, options?: ConfirmTranscriptOptions) => AnamnesisSemanticBlock | null
  recordTranscriptionTechnicalSnapshot: (options: RecordTranscriptionTechnicalSnapshotOptions) => void
  recordSilenceEvent: (event: Omit<AnamnesisSilenceEvent, 'id'>) => void
  markBlockForReprocess: (blockId: string) => void
  startIncrementalAnalysis: (
    options: StartIncrementalAnalysisOptions,
  ) => AnamnesisIncrementalAnalysisRun | null
  completeIncrementalAnalysis: (runId: string, options: CompleteIncrementalAnalysisOptions) => void
  failIncrementalAnalysis: (runId: string, options: FailIncrementalAnalysisOptions) => void
  recordSuggestionDisplayed: (options: RecordSuggestionDisplayedOptions) => void
  updateSuggestionInteraction: (options: UpdateSuggestionInteractionOptions) => void
  resetSession: () => void
  restoreSessionSnapshot: (snapshot: Pick<
    AnamnesisTranscriptState,
    | 'sessionId'
    | 'confirmedText'
    | 'semanticBlocks'
    | 'silenceEvents'
    | 'aiAnalysisQueue'
    | 'analysisRuns'
    | 'suggestionInteractions'
    | 'snapshots'
  >) => void
  correctSemanticBlockSpeaker: (
    blockId: string,
    speaker: AnamnesisSpeakerRole,
    options?: { confidence?: number; incorrectAutoGuess?: boolean },
  ) => void
}

const createAnamnesisId = (prefix: string): string =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const toIso = (date?: Date): string => (date ?? new Date()).toISOString()

const joinTranscript = (current: string, next: string): string => {
  const cleanCurrent = current.trim()
  const cleanNext = next.trim()
  if (!cleanCurrent) return cleanNext
  if (!cleanNext) return cleanCurrent
  return `${cleanCurrent} ${cleanNext}`
}

const createSnapshot = (
  state: Pick<
    AnamnesisTranscriptState,
    'confirmedText' | 'interimText' | 'semanticBlocks' | 'aiAnalysisQueue'
  >,
  reason: AnamnesisTranscriptSnapshot['reason'],
  metadata?: AnamnesisTranscriptTechnicalMetadata,
): AnamnesisTranscriptSnapshot => {
  const transcription = sanitizeTranscriptTechnicalMetadata(metadata)
  return {
    id: createAnamnesisId('snapshot'),
    createdAt: toIso(),
    reason,
    confirmedLength: state.confirmedText.length,
    interimLength: state.interimText.length,
    semanticBlockCount: state.semanticBlocks.length,
    queuedAnalysisCount: state.aiAnalysisQueue.filter((item) => item.status === 'queued').length,
    contextMetadata: createEmptyAnamnesisContextMetadata(),
    ...(Object.keys(transcription).length ? { transcription } : {}),
  }
}

const initialState = {
  sessionId: null,
  status: 'idle' as AnamnesisTranscriptStatus,
  errorMessage: null,
  interimText: '',
  confirmedText: '',
  semanticBlocks: [] as AnamnesisSemanticBlock[],
  silenceEvents: [] as AnamnesisSilenceEvent[],
  aiAnalysisQueue: [] as AnamnesisAiAnalysisQueueItem[],
  analysisRuns: [] as AnamnesisIncrementalAnalysisRun[],
  suggestionInteractions: [] as AnamnesisRealtimeSuggestionInteraction[],
  snapshots: [] as AnamnesisTranscriptSnapshot[],
}

export const useAnamnesisTranscriptStore = create<AnamnesisTranscriptState>()((set, get) => ({
  ...initialState,

  startSession: (options) => {
    const sessionId = options?.sessionId ?? createAnamnesisId('anamnesis-session')
    bindAnamnesisLangfuseConsultation({
      consultationId: sessionId,
      doctorId: options?.doctorId,
      language: options?.language ?? 'pt-BR',
      offlineMode: options?.offlineMode,
      fallbackMode: options?.fallbackMode ?? null,
    })
    set((state) => {
      const nextState = {
        ...state,
        sessionId,
        status: 'processing' as AnamnesisTranscriptStatus,
        errorMessage: null,
      }
      return {
        ...nextState,
        snapshots: [...state.snapshots, createSnapshot(nextState, 'session-started')],
      }
    })
  },

  setStatus: (status, errorMessage = null) => {
    set({ status, errorMessage })
  },

  appendInterim: (text) => {
    set({ interimText: text.trim() })
  },

  clearInterim: () => {
    set({ interimText: '' })
  },

  confirmTranscript: (text, options) => {
    const cleaned = text.trim()
    if (!cleaned) return null

    const now = new Date()
    const transcription = sanitizeTranscriptTechnicalMetadata(options?.metadata)
    const blockId = createAnamnesisId('semantic-block')
    const segmentId = options?.segmentId?.trim() || undefined
    const block: AnamnesisSemanticBlock = {
      id: blockId,
      text: cleaned,
      source: 'speech',
      startedAt: toIso(options?.startedAt ?? now),
      endedAt: toIso(options?.endedAt ?? now),
      ...(Object.keys(transcription).length ? { transcription } : {}),
      ...(options?.speaker
        ? {
            speaker: options.speaker,
            speakerConfidence: options.speakerConfidence,
            segmentId: segmentId ?? blockId,
            ...(typeof options.doctorVoiceMatchScore === 'number' &&
            Number.isFinite(options.doctorVoiceMatchScore)
              ? { doctorVoiceMatchScore: options.doctorVoiceMatchScore }
              : {}),
          }
        : {}),
      status: 'queued',
      revision: 1,
    }

    const queueItem: AnamnesisAiAnalysisQueueItem = {
      id: createAnamnesisId('analysis-queue'),
      blockId: block.id,
      blockIds: [block.id],
      reason: 'new-block',
      status: 'queued',
      createdAt: toIso(now),
      updatedAt: toIso(now),
      revision: block.revision,
    }

    set((state) => {
      const nextState = {
        ...state,
        confirmedText: joinTranscript(state.confirmedText, cleaned),
        interimText: '',
        semanticBlocks: [...state.semanticBlocks, block],
        aiAnalysisQueue: [...state.aiAnalysisQueue, queueItem],
        errorMessage: null,
      }
      return {
        ...nextState,
        snapshots: [...state.snapshots, createSnapshot(nextState, 'transcript-confirmed', transcription)],
      }
    })

    return block
  },

  recordTranscriptionTechnicalSnapshot: (options) => {
    const transcription = sanitizeTranscriptTechnicalMetadata(options.metadata)
    set((state) => ({
      ...state,
      snapshots: [...state.snapshots, createSnapshot(state, options.reason, transcription)],
    }))
  },

  recordSilenceEvent: (event) => {
    set((state) => ({
      silenceEvents: [...state.silenceEvents, { ...event, id: createAnamnesisId('silence') }],
    }))
  },

  markBlockForReprocess: (blockId) => {
    const now = new Date()
    set((state) => {
      const nextBlocks = state.semanticBlocks.map((block) =>
        block.id === blockId
          ? { ...block, status: 'needs-reprocess' as const, revision: block.revision + 1 }
          : block,
      )
      const target = nextBlocks.find((block) => block.id === blockId)
      if (!target) return state

      const queueItem: AnamnesisAiAnalysisQueueItem = {
        id: createAnamnesisId('analysis-queue'),
        blockId,
        blockIds: [blockId],
        reason: 'partial-reprocess',
        status: 'queued',
        createdAt: toIso(now),
        updatedAt: toIso(now),
        revision: target.revision,
      }
      const nextState = {
        ...state,
        semanticBlocks: nextBlocks,
        aiAnalysisQueue: [...state.aiAnalysisQueue, queueItem],
      }
      return {
        ...nextState,
        snapshots: [...state.snapshots, createSnapshot(nextState, 'manual-reprocess')],
      }
    })
  },

  startIncrementalAnalysis: (options) => {
    const uniqueBlockIds = [...new Set(options.blockIds)].filter(Boolean)
    if (!uniqueBlockIds.length || !options.requestSignature.trim()) return null

    const now = options.now ?? new Date()
    let createdRun: AnamnesisIncrementalAnalysisRun | null = null

    set((state) => {
      const hasProcessingRun = state.analysisRuns.some((run) => run.status === 'processing')
      const hasDuplicateRun = state.analysisRuns.some(
        (run) =>
          run.requestSignature === options.requestSignature &&
          (run.status === 'processing' || run.status === 'done'),
      )
      if (hasProcessingRun || hasDuplicateRun) return state

      const selectedBlocks = state.semanticBlocks.filter((block) => uniqueBlockIds.includes(block.id))
      if (!selectedBlocks.length) return state

      const snapshotReason = options.reason === 'auto-pause' ? 'auto-analysis' : 'manual-analysis'
      const snapshot = createSnapshot(state, snapshotReason)
      const queueItem: AnamnesisAiAnalysisQueueItem = {
        id: createAnamnesisId('analysis-queue'),
        blockId: selectedBlocks[0].id,
        blockIds: selectedBlocks.map((block) => block.id),
        snapshotId: snapshot.id,
        reason: options.reason,
        status: 'processing',
        createdAt: toIso(now),
        updatedAt: toIso(now),
        revision: Math.max(...selectedBlocks.map((block) => block.revision)),
        requestSignature: options.requestSignature,
      }
      const run: AnamnesisIncrementalAnalysisRun = {
        id: createAnamnesisId('analysis-run'),
        queueItemId: queueItem.id,
        snapshotId: snapshot.id,
        reason: options.reason,
        status: 'processing',
        blockIds: queueItem.blockIds,
        requestSignature: options.requestSignature,
        createdAt: toIso(now),
        updatedAt: toIso(now),
        startedAt: toIso(now),
      }

      createdRun = run
      return {
        ...state,
        semanticBlocks: state.semanticBlocks.map((block) =>
          queueItem.blockIds.includes(block.id) ? { ...block, status: 'queued' as const } : block,
        ),
        aiAnalysisQueue: [
          ...state.aiAnalysisQueue.map((item) =>
            queueItem.blockIds.includes(item.blockId) && item.status === 'queued'
              ? { ...item, status: 'processing' as const, updatedAt: toIso(now) }
              : item,
          ),
          queueItem,
        ],
        analysisRuns: [...state.analysisRuns, run],
        snapshots: [...state.snapshots, snapshot],
      }
    })

    return createdRun
  },

  completeIncrementalAnalysis: (runId, options) => {
    const now = options.now ?? new Date()
    set((state) => {
      const run = state.analysisRuns.find((item) => item.id === runId)
      if (!run) return state

      return {
        ...state,
        semanticBlocks: state.semanticBlocks.map((block) =>
          run.blockIds.includes(block.id) ? { ...block, status: 'processed' as const } : block,
        ),
        aiAnalysisQueue: state.aiAnalysisQueue.map((item) =>
          item.id === run.queueItemId || item.blockIds.some((blockId) => run.blockIds.includes(blockId))
            ? { ...item, status: 'done' as const, updatedAt: toIso(now), errorMessage: undefined }
            : item,
        ),
        analysisRuns: state.analysisRuns.map((item) =>
          item.id === runId
            ? {
                ...item,
                status: 'done' as const,
                updatedAt: toIso(now),
                completedAt: toIso(now),
                result: options.result,
                errorMessage: undefined,
              }
            : item,
        ),
      }
    })
  },

  failIncrementalAnalysis: (runId, options) => {
    const now = options.now ?? new Date()
    set((state) => {
      const run = state.analysisRuns.find((item) => item.id === runId)
      if (!run) return state

      return {
        ...state,
        semanticBlocks: state.semanticBlocks.map((block) =>
          run.blockIds.includes(block.id) ? { ...block, status: 'needs-reprocess' as const } : block,
        ),
        aiAnalysisQueue: state.aiAnalysisQueue.map((item) =>
          item.id === run.queueItemId || item.blockIds.some((blockId) => run.blockIds.includes(blockId))
            ? {
                ...item,
                status: 'failed' as const,
                updatedAt: toIso(now),
                errorMessage: options.errorMessage,
              }
            : item,
        ),
        analysisRuns: state.analysisRuns.map((item) =>
          item.id === runId
            ? {
                ...item,
                status: 'failed' as const,
                updatedAt: toIso(now),
                completedAt: toIso(now),
                errorMessage: options.errorMessage,
              }
            : item,
        ),
      }
    })
  },

  recordSuggestionDisplayed: (options) => {
    const key = options.key.trim()
    const text = options.text.trim()
    const title = options.title.trim()
    if (!key || !text || !title) return

    const nowIso = toIso(options.now)
    set((state) => {
      const existing = state.suggestionInteractions.find((item) => item.key === key)
      if (existing) return state

      return {
        ...state,
        suggestionInteractions: [
          ...state.suggestionInteractions,
          {
            key,
            kind: options.kind,
            priority: options.priority,
            status: 'displayed',
            title,
            text,
            sourceRunId: options.sourceRunId,
            firstDisplayedAt: nowIso,
            lastUpdatedAt: nowIso,
            displayCount: 1,
            persistenceContractVersion: 1,
          },
        ],
      }
    })
  },

  updateSuggestionInteraction: (options) => {
    const key = options.key.trim()
    if (!key) return

    const nowIso = toIso(options.now)
    set((state) => ({
      ...state,
      suggestionInteractions: state.suggestionInteractions.map((item) =>
        item.key === key ? { ...item, status: options.status, lastUpdatedAt: nowIso } : item,
      ),
    }))
  },

  resetSession: () => {
    const previousSessionId = get().sessionId
    if (previousSessionId) unbindAnamnesisLangfuseConsultation(previousSessionId)
    const nextState = { ...initialState }
    set({
      ...nextState,
      snapshots: [createSnapshot(nextState, 'session-reset')],
    })
  },

  restoreSessionSnapshot: (snapshot) => {
    const previousSessionId = get().sessionId
    if (previousSessionId && previousSessionId !== snapshot.sessionId) {
      unbindAnamnesisLangfuseConsultation(previousSessionId)
    }
    if (snapshot.sessionId) {
      bindAnamnesisLangfuseConsultation({ consultationId: snapshot.sessionId })
    }
    set({
      sessionId: snapshot.sessionId,
      status: 'idle',
      errorMessage: null,
      interimText: '',
      confirmedText: snapshot.confirmedText,
      semanticBlocks: snapshot.semanticBlocks,
      silenceEvents: snapshot.silenceEvents,
      aiAnalysisQueue: snapshot.aiAnalysisQueue,
      analysisRuns: snapshot.analysisRuns,
      suggestionInteractions: snapshot.suggestionInteractions,
      snapshots: snapshot.snapshots,
    })
  },

  correctSemanticBlockSpeaker: (blockId, speaker, options) => {
    const confidence = options?.confidence
    const incorrectAuto = options?.incorrectAutoGuess
    const block = get().semanticBlocks.find((item) => item.id === blockId)
    const previousSpeaker = block?.speaker ?? 'unknown'
    const nextRevision = (block?.revision ?? 0) + 1

    set((state) => ({
      ...state,
      semanticBlocks: state.semanticBlocks.map((item) =>
        item.id === blockId
          ? {
              ...item,
              speaker,
              speakerConfidence:
                typeof confidence === 'number' && Number.isFinite(confidence) ? confidence : 0.95,
              segmentId: item.segmentId ?? item.id,
              revision: item.revision + 1,
              ...(typeof incorrectAuto === 'boolean'
                ? { incorrectAutoGuess: incorrectAuto }
                : {}),
            }
          : item,
      ),
    }))

    trackAnamnesisTechnicalEvent('speaker_correction_applied', {
      previousSpeakerKind: previousSpeaker,
      newSpeakerKind: speaker,
      semanticBlockId: blockId,
      blockRevision: nextRevision,
      incorrectAutoGuess: typeof incorrectAuto === 'boolean' ? incorrectAuto : false,
    })
  },
}))

export interface AnamnesisConversationSegment {
  segmentId: string
  semanticBlockId: string
  speaker: AnamnesisSpeakerRole
  speakerConfidence?: number
  startedAt: string
  endedAt: string
}

export const mapSemanticBlocksToConversationSegments = (
  blocks: AnamnesisSemanticBlock[],
): AnamnesisConversationSegment[] =>
  blocks.map((block) => ({
    segmentId: block.segmentId ?? block.id,
    semanticBlockId: block.id,
    speaker: block.speaker ?? 'unknown',
    speakerConfidence: block.speakerConfidence,
    startedAt: block.startedAt,
    endedAt: block.endedAt,
  }))

export const selectAnamnesisTranscriptSummary = () => {
  const state = useAnamnesisTranscriptStore.getState()
  return {
    sessionId: state.sessionId,
    confirmedText: state.confirmedText,
    semanticBlocks: state.semanticBlocks,
    silenceEvents: state.silenceEvents,
    aiAnalysisQueue: state.aiAnalysisQueue,
    analysisRuns: state.analysisRuns,
    suggestionInteractions: state.suggestionInteractions,
    snapshots: state.snapshots,
  }
}
